import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import referralService from '../../services/referrals';
import { useToast } from '../../hooks/useToast';
import type { ReferralOverview } from '../../types';

const formatMessage = (template: string, link: string) => template.replace('{{link}}', link);

const getBadgeKey = (conversions: number) => {
  if (conversions >= 25) return 'legend';
  if (conversions >= 10) return 'elite';
  if (conversions >= 5) return 'rising';
  return 'starter';
};

export default function ReferralPage() {
  const { t } = useTranslation('referrals');
  const toastify = useToast();
  const [customCode, setCustomCode] = useState('');
  const [codeExpiry, setCodeExpiry] = useState('');
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutMethod] = useState('crypto');
  const [payoutDestination, setPayoutDestination] = useState('');

  const { data: overview, isLoading, error, refetch } = useQuery({
    queryKey: ['referrals', 'overview'],
    queryFn: () => referralService.getOverview(),
  });

  const { data: analyticsData } = useQuery({
    queryKey: ['referrals', 'analytics'],
    queryFn: () => referralService.getAnalytics(),
  });

  const { data: rewardsData } = useQuery({
    queryKey: ['referrals', 'rewards'],
    queryFn: () => referralService.getRewards({ per_page: 5 }),
  });

  const { data: affiliateReport } = useQuery({
    queryKey: ['referrals', 'affiliate-report'],
    queryFn: () => referralService.getAffiliateReport(),
  });

  const overviewData = overview as ReferralOverview | undefined;
  const analytics = analyticsData?.analytics;

  const shareLinks = useMemo(() => {
    if (!overviewData?.code || !overviewData?.program) {
      return null;
    }

    const link = overviewData.code.short_url;
    const messages = overviewData.program.sharing_messages || {};

    return {
      link,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(formatMessage(messages.twitter || '', link))}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(formatMessage(messages.whatsapp || '', link))}`,
      emailSubject: messages.email_subject || t('share.email_subject'),
      emailBody: formatMessage(messages.email_body || t('share.email_body'), link),
    };
  }, [overviewData, t]);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toastify.successRaw(t('share.copied'));
    } catch {
      toastify.errorRaw(t('share.copy_failed'));
    }
  };

  const handleUpdateCode = async () => {
    if (!customCode.trim()) {
      toastify.errorRaw(t('code.custom_required'));
      return;
    }

    try {
      await referralService.updateCode({
        code: customCode.trim(),
        expires_at: codeExpiry || undefined,
      });
      setCustomCode('');
      setCodeExpiry('');
      toastify.successRaw(t('code.custom_success'));
      refetch();
    } catch {
      toastify.errorRaw(t('code.custom_error'));
    }
  };

  const handleRegenerate = async () => {
    try {
      await referralService.regenerateCode();
      toastify.successRaw(t('code.regenerated'));
      refetch();
    } catch {
      toastify.errorRaw(t('code.regenerate_error'));
    }
  };

  const handleInvite = async () => {
    const emails = inviteEmails
      .split(',')
      .map(email => email.trim())
      .filter(Boolean);

    if (emails.length === 0) {
      toastify.errorRaw(t('invite.email_required'));
      return;
    }

    try {
      await referralService.invite({
        emails,
        message: inviteMessage || undefined,
        channel: 'email',
      });
      setInviteEmails('');
      setInviteMessage('');
      toastify.successRaw(t('invite.sent'));
    } catch {
      toastify.errorRaw(t('invite.error'));
    }
  };

  const handlePayoutRequest = async () => {
    const amount = Number(payoutAmount);
    if (!amount || amount <= 0) {
      toastify.errorRaw(t('affiliate.amount_required'));
      return;
    }

    try {
      await referralService.requestAffiliatePayout({
        amount,
        method: payoutMethod,
        payout_details: payoutDestination ? { destination: payoutDestination } : undefined,
      });
      setPayoutAmount('');
      setPayoutDestination('');
      toastify.successRaw(t('affiliate.payout_requested'));
    } catch {
      toastify.errorRaw(t('affiliate.payout_error'));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error || !overviewData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {t('error_loading')}
        </div>
      </div>
    );
  }

  if (!overviewData.code || !overviewData.program) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('title')}</h2>
          <p className="mt-2 text-sm text-gray-500">{t('error_loading')}</p>
        </div>
      </div>
    );
  }

  const { code, stats, rewards_summary: rewardsSummary, leaderboards, recent_referrals: recentReferrals } = overviewData;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-gray-600 mt-1">{t('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleRegenerate}
            className="px-4 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            {t('code.regenerate')}
          </button>
          <button
            onClick={() => handleCopy(code.short_url)}
            className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {t('share.copy_link')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('code.title')}</h2>
          <div className="space-y-2">
            <p className="text-sm text-gray-500">{t('code.your_code')}</p>
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
              <span className="font-semibold text-gray-900">{code.code}</span>
              <button
                onClick={() => handleCopy(code.code)}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                {t('share.copy')}
              </button>
            </div>
            <p className="text-sm text-gray-500">{t('code.short_link')}</p>
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
              <span className="text-sm text-gray-700 truncate">{code.short_url}</span>
              <button
                onClick={() => handleCopy(code.short_url)}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                {t('share.copy')}
              </button>
            </div>
            <p className="text-sm text-gray-500">{t('code.share_link')}</p>
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
              <span className="text-sm text-gray-700 truncate">{code.share_url}</span>
              <button
                onClick={() => handleCopy(code.share_url)}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                {t('share.copy')}
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <label className="text-sm font-medium text-gray-700">{t('code.custom_label')}</label>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={customCode}
                onChange={(event) => setCustomCode(event.target.value)}
                placeholder={t('code.custom_placeholder')}
                className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <button
                onClick={handleUpdateCode}
                className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800"
              >
                {t('code.custom_button')}
              </button>
            </div>
            <div className="mt-3">
              <label className="text-sm text-gray-500">{t('code.expiration_label')}</label>
              <input
                type="date"
                value={codeExpiry}
                onChange={(event) => setCodeExpiry(event.target.value)}
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-400 mt-1">{t('code.expiration_hint')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('share.title')}</h2>
          {shareLinks && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {/* Twitter / X */}
                <a
                  href={shareLinks.twitter}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
                >
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-black">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.912-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </span>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">Twitter / X</span>
                </a>

                {/* LinkedIn */}
                <a
                  href={shareLinks.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-blue-50 transition-colors group"
                >
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[#0A66C2]">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </span>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">LinkedIn</span>
                </a>

                {/* Facebook */}
                <a
                  href={shareLinks.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-blue-50 transition-colors group"
                >
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[#1877F2]">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </span>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Facebook</span>
                </a>

                {/* WhatsApp */}
                <a
                  href={shareLinks.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-green-50 transition-colors group"
                >
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[#25D366]">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </span>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-green-700">WhatsApp</span>
                </a>
              </div>

              {code.qr_url && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-2">{t('share.qr_label')}</p>
                  <img className="w-32 h-32 border border-gray-200 rounded-md" src={code.qr_url} alt={t('share.qr_alt')} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('invite.title')}</h2>
          <div>
            <label className="text-sm text-gray-500">{t('invite.emails_label')}</label>
            <input
              type="text"
              value={inviteEmails}
              onChange={(event) => setInviteEmails(event.target.value)}
              placeholder={t('invite.emails_placeholder')}
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="text-sm text-gray-500">{t('invite.message_label')}</label>
            <textarea
              value={inviteMessage}
              onChange={(event) => setInviteMessage(event.target.value)}
              placeholder={t('invite.message_placeholder')}
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              rows={3}
            />
          </div>
          <button
            onClick={handleInvite}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            {t('invite.send_button')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">{t('stats.clicks')}</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.clicks}</p>
          <p className="text-xs text-gray-400">{t('stats.signup_rate', { value: stats.signup_rate })}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">{t('stats.signups')}</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.signups}</p>
          <p className="text-xs text-gray-400">{t('stats.verified', { value: stats.verified })}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">{t('stats.conversions')}</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.conversions}</p>
          <p className="text-xs text-gray-400">{t('stats.conversion_rate', { value: stats.conversion_rate })}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">{t('stats.rewards')}</p>
          <p className="text-2xl font-semibold text-gray-900">${rewardsSummary.total_earned.toFixed(2)}</p>
          <p className="text-xs text-gray-400">{t('stats.available_credits', { value: rewardsSummary.available_credits })}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('leaderboard.monthly')}</h2>
          <div className="mt-4 space-y-2">
            {leaderboards.monthly.length === 0 && (
              <p className="text-sm text-gray-500">{t('leaderboard.empty')}</p>
            )}
            {leaderboards.monthly.map(entry => (
              <div key={entry.user_id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">#{entry.rank}</span>
                  <span className="text-sm text-gray-900">{entry.name}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
                    {t(`badges.${getBadgeKey(entry.conversions)}`)}
                  </span>
                </div>
                <span className="text-sm text-gray-500">{t('leaderboard.conversions', { value: entry.conversions })}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('leaderboard.all_time')}</h2>
          <div className="mt-4 space-y-2">
            {leaderboards.all_time.length === 0 && (
              <p className="text-sm text-gray-500">{t('leaderboard.empty')}</p>
            )}
            {leaderboards.all_time.map(entry => (
              <div key={entry.user_id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">#{entry.rank}</span>
                  <span className="text-sm text-gray-900">{entry.name}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
                    {t(`badges.${getBadgeKey(entry.conversions)}`)}
                  </span>
                </div>
                <span className="text-sm text-gray-500">{t('leaderboard.referrals', { value: entry.referrals })}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('analytics.title')}</h2>
          {analytics ? (
            <>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <p className="text-gray-500">{t('analytics.signup_rate')}</p>
                  <p className="text-lg font-semibold text-gray-900">{analytics.signup_rate}%</p>
                </div>
                <div>
                  <p className="text-gray-500">{t('analytics.conversion_rate')}</p>
                  <p className="text-lg font-semibold text-gray-900">{analytics.conversion_rate}%</p>
                </div>
                <div>
                  <p className="text-gray-500">{t('analytics.average_ltv')}</p>
                  <p className="text-lg font-semibold text-gray-900">${analytics.average_ltv.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-500">{t('analytics.roi')}</p>
                  <p className="text-lg font-semibold text-gray-900">{analytics.roi}%</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <p className="text-gray-500 mb-2">{t('analytics.channel_breakdown')}</p>
                  <div className="space-y-1">
                    {analytics.channel_breakdown.slice(0, 4).map(channel => (
                      <div key={channel.source ?? 'unknown'} className="flex items-center justify-between">
                        <span>{channel.source ?? t('tracking.unknown')}</span>
                        <span className="text-gray-900">{channel.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 mb-2">{t('analytics.geo_distribution')}</p>
                  <div className="space-y-1">
                    {analytics.geo_distribution.slice(0, 4).map(geo => (
                      <div key={geo.country} className="flex items-center justify-between">
                        <span>{geo.country}</span>
                        <span className="text-gray-900">{geo.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {analytics.success_metrics && (
                <div className="mt-6 border-t border-gray-100 pt-4 grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <p className="text-gray-500">{t('analytics.success.signup_rate')}</p>
                    <p className="text-lg font-semibold text-gray-900">{analytics.success_metrics.referral_signup_rate}%</p>
                  </div>
                  <div>
                    <p className="text-gray-500">{t('analytics.success.conversion_rate')}</p>
                    <p className="text-lg font-semibold text-gray-900">{analytics.success_metrics.referral_to_paid_conversion_rate}%</p>
                  </div>
                  <div>
                    <p className="text-gray-500">{t('analytics.success.viral_coefficient')}</p>
                    <p className="text-lg font-semibold text-gray-900">{analytics.success_metrics.viral_coefficient}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">{t('analytics.success.cac_reduction')}</p>
                    <p className="text-lg font-semibold text-gray-900">{analytics.success_metrics.cac_reduction}%</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">{t('analytics.loading')}</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('rewards.title')}</h2>
          <div className="mt-4 space-y-3 text-sm text-gray-600">
            <div className="flex items-center justify-between">
              <span>{t('rewards.available_credit')}</span>
              <span className="font-semibold text-gray-900">${rewardsSummary.available_credits.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('rewards.available_discount')}</span>
              <span className="font-semibold text-gray-900">${rewardsSummary.available_discounts.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('rewards.pending')}</span>
              <span className="font-semibold text-gray-900">{rewardsSummary.pending_rewards}</span>
            </div>
          </div>
          {rewardsData?.data?.length ? (
            <div className="mt-4 border-t border-gray-100 pt-4 space-y-2">
              {rewardsData.data.map(reward => (
                <div key={reward.id} className="flex items-center justify-between text-sm text-gray-600">
                  <span>{reward.type}</span>
                  <span className="font-medium text-gray-900">${Number(reward.amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('affiliate.title')}</h2>
          <div className="mt-4 space-y-3 text-sm text-gray-600">
            <div className="flex items-center justify-between">
              <span>{t('affiliate.total_commissions')}</span>
              <span className="font-semibold text-gray-900">
                ${Number(affiliateReport?.report.total_commissions ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('affiliate.total_credits')}</span>
              <span className="font-semibold text-gray-900">
                ${Number(affiliateReport?.report.total_credits ?? 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('affiliate.request_payout')}</h2>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-sm text-gray-500">{t('affiliate.amount_label')}</label>
              <input
                type="number"
                value={payoutAmount}
                onChange={(event) => setPayoutAmount(event.target.value)}
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="text-sm text-gray-500">{t('affiliate.destination_label')}</label>
              <input
                type="text"
                value={payoutDestination}
                onChange={(event) => setPayoutDestination(event.target.value)}
                placeholder={t('affiliate.destination_crypto_placeholder')}
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <button
              onClick={handlePayoutRequest}
              className="w-full px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800"
            >
              {t('affiliate.submit_payout')}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">{t('tracking.title')}</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm text-left text-gray-600">
            <thead className="text-xs uppercase text-gray-400 border-b">
              <tr>
                <th className="py-2 pr-4">{t('tracking.referee')}</th>
                <th className="py-2 pr-4">{t('tracking.status')}</th>
                <th className="py-2 pr-4">{t('tracking.source')}</th>
                <th className="py-2">{t('tracking.conversion')}</th>
              </tr>
            </thead>
            <tbody>
              {recentReferrals.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-gray-500">
                    {t('tracking.empty')}
                  </td>
                </tr>
              )}
              {recentReferrals.map(referral => (
                <tr key={referral.id} className="border-b last:border-b-0">
                  <td className="py-3 pr-4">{referral.referee?.email ?? t('tracking.pending')}</td>
                  <td className="py-3 pr-4 capitalize">{referral.status}</td>
                  <td className="py-3 pr-4">{referral.source ?? t('tracking.unknown')}</td>
                  <td className="py-3">{referral.converted_at ? t('tracking.converted') : t('tracking.not_converted')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
